/**
 * BookingDraftCard — one row in the bookings list. Click opens the detail Sheet.
 * Read-only; lifecycle actions live in the detail panel.
 */

import { useFormatters, useT } from '@hostesa/i18n';
import { StatusBadge } from '@hostesa/ui';
import type { Booking } from './_deps';
import { asString } from './_deps';
import { bookingStatusVariant } from './booking-status';
import { namespace } from './i18n';

export interface BookingDraftCardProps {
  booking: Booking;
  onOpen: (booking: Booking) => void;
}

export function BookingDraftCard({ booking, onOpen }: BookingDraftCardProps) {
  const { t } = useT(namespace);
  const { formatDate } = useFormatters('UTC');

  const checkIn = asString(booking.checkIn);
  const checkOut = asString(booking.checkOut);
  const roomType = asString(booking.roomType);

  return (
    <button
      type="button"
      data-testid={`booking-card-${booking.id}`}
      onClick={() => onOpen(booking)}
      aria-label={t('list.open')}
      className="flex w-full items-center justify-between gap-4 rounded-md border border-[var(--color-border)] bg-[var(--color-alabaster)] p-4 text-left shadow-[var(--shadow-card)] transition-colors hover:border-[var(--color-warm-stone)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
    >
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-[var(--color-charcoal)]">
          {roomType ?? t('list.notProvided')}
        </span>
        <span className="font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
          {checkIn ? formatDate(checkIn) : t('list.notProvided')}
          {' → '}
          {checkOut ? formatDate(checkOut) : t('list.notProvided')}
        </span>
      </div>
      <StatusBadge
        data-testid={`booking-status-${booking.id}`}
        status={bookingStatusVariant(booking.status)}
        label={t(`status.${booking.status}`)}
      />
    </button>
  );
}
